import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  parseDivaReport,
  reparseDivaReport,
  saveDivaReport,
  updateUsageMetadata,
} from "../services/adhd-assessment";

export function useUpdateAssessmentOutcome() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      usage_id,
      assessment_outcome,
      assessment_notes,
    }: {
      usage_id: string;
      assessment_outcome: string;
      assessment_notes?: string;
    }) =>
      updateUsageMetadata(usage_id, { assessment_outcome, assessment_notes }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      toast({ title: "Success", description: "Exploration outcome recorded" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to record exploration outcome",
        variant: "destructive",
      });
    },
  });
}

export function useRecordDivaResult() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      usage_id,
      diva_outcome,
      diva_scores,
    }: {
      usage_id: string;
      diva_outcome: string;
      diva_scores?: import("../services/adhd-assessment").DivaScores;
    }) =>
      updateUsageMetadata(usage_id, {
        diva_outcome,
        diva_scores,
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      toast({ title: "Success", description: "DIVA result recorded" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to record the DIVA result",
        variant: "destructive",
      });
    },
  });
}

export function useParseDivaReport() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (usage_id: string) => parseDivaReport(usage_id),
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to parse the DIVA doc",
        variant: "destructive",
      });
    },
  });
}

export function useReparseDivaReport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (usage_id: string) => reparseDivaReport(usage_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      toast({ title: "Success", description: "Report re-parsed" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to re-parse the report",
        variant: "destructive",
      });
    },
  });
}

export function useSaveDivaReport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ usage_id, file }: { usage_id: string; file: File }) =>
      saveDivaReport(usage_id, file),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      toast({ title: "Success", description: "DIVA report PDF saved" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to save the DIVA report",
        variant: "destructive",
      });
    },
  });
}
